import {Component,Inject} from "@angular/core";
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {ReportParam,ReportParamType} from "@domain/reporting/reportParam";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {ReportingService} from "@services/admin/reporting/reporting.service";
import {first} from "rxjs/operators";
import {TypeCodeErreur} from "@domain/common/http/result";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";

/**
 * Composant d'affichage du formulaire de modification d'un paramètre
 */
@Component({
    host: {'data-test-id': 'report-param-modify'},
    selector: 'report-param-modify',
    templateUrl: './report-param-modify.component.html'
})
export class ReportParamModifyComponent {
    /** Booléen indiquant si on est en mode création ou en mode modification */
    isCreation: boolean;

    /** Objet paramètre */
    reportParam: ReportParam;

    /** Enum pour les différents types de paramètres */
    ReportParamType = ReportParamType;

    /** Nom original du paramètre */
    originalName: string;

    /** SQL original du paramètre (en modification) */
    private originalSql: string;

    /** Options de type */
    typeOptions: { type: number, libelle: string }[] = [{
        type: ReportParamType.TYPE_TEXTE,
        libelle: this.translateService.instant('admin.bibliotheque.reporting.infosReporting.parametres.type.1')
    },{
        type: ReportParamType.TYPE_DATE,
        libelle: this.translateService.instant('admin.bibliotheque.reporting.infosReporting.parametres.type.2')
    },{
        type: ReportParamType.TYPE_NUMERIQUE,
        libelle: this.translateService.instant('admin.bibliotheque.reporting.infosReporting.parametres.type.3')
    },{
        type: ReportParamType.TYPE_LISTE_CHOIX_UNIQUE,
        libelle: this.translateService.instant('admin.bibliotheque.reporting.infosReporting.parametres.type.4')
    },{
        type: ReportParamType.TYPE_LISTE_CHOIX_MULTIPLE,
        libelle: this.translateService.instant('admin.bibliotheque.reporting.infosReporting.parametres.type.5')
    },];

    /** Indique si l'utilisateur est admin */
    isAdmin: boolean = false;

    /** Type original du paramètre (en modification) */
    private originalType: number;

    /**
     * Constructeur
     */
    constructor(protected matDialogRef: MatDialogRef<ReportParamModifyComponent>,
                private translateService: TranslateService,
                private toastrService: ToastrService,
                private reportingService: ReportingService,
                private store: Store<AppState>,
                @Inject(MAT_DIALOG_DATA) public data: { idReport: number, reportParam: ReportParam }) {
        this.reportParam = !!data.reportParam ? data.reportParam : new ReportParam(null);
        this.isCreation = !data.reportParam;
        this.originalName = !this.isCreation && data?.reportParam?.name;
        this.originalType = !this.isCreation ? data?.reportParam?.type : null;
        this.originalSql = !this.isCreation ? data?.reportParam?.sql : null;

        //Récupération des droits
        this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe(session => {
            this.isAdmin = session.isAdmin;

            //Si sous-admin
            if (!this.isAdmin) {
                if (this.isCreation) {
                    //En création : retirer les types avec SQL
                    this.typeOptions = this.typeOptions.filter(
                        o => o.type !== ReportParamType.TYPE_LISTE_CHOIX_UNIQUE
                            && o.type !== ReportParamType.TYPE_LISTE_CHOIX_MULTIPLE
                    );
                } else {
                    //En modification : empêcher le passage vers un type avec SQL si le type original n'en a pas
                    const originalHasSql = this.originalType === ReportParamType.TYPE_LISTE_CHOIX_UNIQUE
                        || this.originalType === ReportParamType.TYPE_LISTE_CHOIX_MULTIPLE;
                    if (!originalHasSql) {
                        //Retirer les types avec SQL des options
                        this.typeOptions = this.typeOptions.filter(
                            o => o.type !== ReportParamType.TYPE_LISTE_CHOIX_UNIQUE
                                && o.type !== ReportParamType.TYPE_LISTE_CHOIX_MULTIPLE
                        );
                    }
                }
            }
        });
    }

    /**
     * Enregistrement
     */
    save() {
        this.reportingService.saveParam(this.data.idReport,this.reportParam,this.originalName)
            .pipe(first())
            .subscribe(res => {
                if (res.codeErreur !== TypeCodeErreur.NO_ERROR) {
                    //On affiche le message d'erreur
                    TypeCodeErreur.showError(res.codeErreur,this.translateService,this.toastrService);
                } else {
                    //On ferme la boîte de dialogue en indiquant qu'il faut mettre à jour la liste
                    this.matDialogRef.close(true);

                    //Si aucune erreur n'est renvoyée alors on affiche un message de succès
                    this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
                }
            });
    }

    /**
     * Purge du champ SQL en cas de type incompatible
     */
    onTypeChange() {
        if (this.reportParam.type == ReportParamType.TYPE_LISTE_CHOIX_UNIQUE || this.reportParam.type == ReportParamType.TYPE_LISTE_CHOIX_MULTIPLE) {
            //Si on revient vers un type SQL et que le SQL est vide, restaurer l'original
            if (!this.reportParam.sql && this.originalSql) {
                this.reportParam.sql = this.originalSql;
            }
        } else {
            this.reportParam.sql = undefined;
        }
    }
}
